import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { Fragment } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { EmptyState } from '@sorare/core/src/components/EmptyState';
import { LineupHeader } from '@sorare/core/src/components/lineup/LineupHeader';
import {
  ANY_SPORT_MARKET,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { range } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { PlayerCardPlaceholder } from '#src/components/lineup/PlayerCardPlaceholder/index.tsx';
import { Badge } from '#src/components/managerTeam/Badge/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import type {
  ChampionshipNoLineupsQuery,
  ChampionshipNoLineupsQueryVariables,
} from './__generated__/index.graphql.ts';

const EmptyLineupLayout = styled(Vertical)`
  width: 100%;
`;
const HorizontalLineup = styled(Horizontal)`
  --gap: var(--unit);
  --item-width: calc(
    (100% - (var(--nb-items-max-per-row) - 1) * var(--gap)) /
      var(--nb-items-max-per-row)
  );
  width: 98%;

  max-width: var(--lineup-max-width);
  margin: auto;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--gap);

  > * {
    flex-basis: var(--item-width);
  }
`;
const Ctas = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: center;
  margin: var(--unit);
`;
const EmptyAddTeam = styled(Vertical).attrs({ gap: 2, center: true })`
  min-height: 240px;
  border-radius: var(--double-unit);
  border: 1px dashed var(--c-nd-150);
  padding: var(--double-unit);
`;

const query = gql`
  query ChampionshipNoLineupsQuery($userGroupSlug: String!) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        startSo5Fixture {
          slug
          gameWeek
          ...isFixtureOpened_so5Fixture
        }
        upcomingSo5Leaderboard {
          slug
          displayName
          mainRarityType
          so5LineupsCount
          so5Fixture {
            slug
            gameWeek
            ...isFixtureOpened_so5Fixture
            type
          }
          canCompose {
            value
            reason
          }
          ...ManagerTeamBadge_so5Leaderboard
          ...PlayerCardPlaceholder_so5Leaderboard
        }
      }
    }
  }
  ${isFixtureOpened.fragments.so5Fixture}
  ${Badge.fragments.so5Leaderboard}
  ${PlayerCardPlaceholder.fragments.so5Leaderboard}
` as TypedDocumentNode<
  ChampionshipNoLineupsQuery,
  ChampionshipNoLineupsQueryVariables
>;

const messages = defineMessages({
  title: {
    defaultMessage: 'No registered lineups',
  },
  openingSoonTitle: {
    defaultMessage: 'Next competition is opening soon',
  },
});

type Props = {
  userGroupSlug: string;
  onComposeClick?: () => void;
  onMarketClick?: () => void;
  nbCards: number;
  gameWeek?: number;
  openingSoon?: boolean;
};
export const ChampionshipNoLineups = ({
  userGroupSlug,
  onComposeClick,
  onMarketClick,
  nbCards,
  gameWeek,
  openingSoon = false,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const { data, loading } = useQuery(query, {
    variables: {
      userGroupSlug,
    },
  });

  const { upcomingSo5Leaderboard } = data?.so5.so5UserGroup || {};

  const isChaseOpen =
    upcomingSo5Leaderboard?.so5Fixture &&
    isFixtureOpened(upcomingSo5Leaderboard?.so5Fixture);
  const isComposable =
    !gameWeek || gameWeek === upcomingSo5Leaderboard?.so5Fixture.gameWeek;

  const fillHeightPercent = 0;
  return (
    <EmptyAddTeam>
      {(!isChaseOpen || !isComposable) && !loading ? (
        <EmptyState
          title={
            <FormattedMessage
              {...(openingSoon ? messages.openingSoonTitle : messages.title)}
            />
          }
        />
      ) : (
        <EmptyLineupLayout gap={2}>
          {!!upcomingSo5Leaderboard && (
            <LineupHeader
              badge={
                <Badge
                  hasLiveGames={false}
                  managerTeam={null}
                  ranking={undefined}
                  leaderboard={upcomingSo5Leaderboard}
                />
              }
              leaderboardName={upcomingSo5Leaderboard.displayName}
              lineupCount={upcomingSo5Leaderboard?.so5LineupsCount || 0}
            />
          )}
          <Vertical gap={2}>
            <HorizontalLineup
              className={classNames({ smartWidth: !!fillHeightPercent })}
              style={
                {
                  '--height-percentage': fillHeightPercent,
                  '--nb-items-max-per-row': nbCards,
                  '--lineup-max-width': '100%',
                } as CSSProperties
              }
            >
              {range(nbCards).map(
                i =>
                  upcomingSo5Leaderboard && (
                    <PlayerCardPlaceholder
                      key={`card_${i}`}
                      position={i}
                      lineup={null}
                      managerTeam={null}
                      leaderboard={upcomingSo5Leaderboard}
                      defaultRarity={
                        upcomingSo5Leaderboard?.mainRarityType || undefined
                      }
                      fixtureOpened
                      loading={false}
                      Footer={Fragment}
                    />
                  )
              )}
            </HorizontalLineup>
            <Ctas>
              {!!upcomingSo5Leaderboard?.canCompose?.value && (
                <Button
                  color="tertiary"
                  size="small"
                  to={generateSportContextPath(
                    ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
                    {
                      params: {
                        eventType: upcomingSo5Leaderboard?.so5Fixture.type,
                        leaderboard: upcomingSo5Leaderboard?.slug || '',
                      },
                    }
                  )}
                  onClick={onComposeClick}
                >
                  <FormattedMessage {...fantasy.composeLineup} />
                </Button>
              )}
              <Button
                color="primary"
                size="small"
                to={generateSportContextPath(ANY_SPORT_MARKET)}
                onClick={onMarketClick}
              >
                <FormattedMessage {...glossary.goToMarket} />
              </Button>
            </Ctas>
          </Vertical>
        </EmptyLineupLayout>
      )}
    </EmptyAddTeam>
  );
};
